// See NotificationBar.spec.md for NB-FR-006 and NB-FR-007

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ApiClient from 'api-client/ApiClient'
import { UserPreferences } from 'app/constants'
import { parseTimestamp } from 'core/audit-events/helpers'
import type { AuditEvent } from 'core/audit-events/models'
import { STALE_REQUEST_MS } from 'core/audit-events/useAuditEventStream'
import useEventSessionScope from 'core/audit-events/useEventSessionScope'
import useIsMounted from 'core/hooks/useIsMounted'
import { countNewer, laterOf, newestTimestamp } from './helpers'

const { preferenceStore } = ApiClient.getInstance()

export interface NotificationCenterPreference {
  lastSeenAt: string
}

type StoredLastSeen = { found: true; lastSeenAt: string } | { found: false }

const isNotFound = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 404

// NB-FR-007: A stored value counts only if it is JSON holding a parseable `lastSeenAt`. Anything
// else (empty, malformed, missing or invalid time) is treated as "not stored", so the starting
// point overwrites it instead of every read and save failing on it for good.
const parseStoredValue = (raw?: string): StoredLastSeen => {
  if (!raw) return { found: false }
  try {
    const { lastSeenAt } = (JSON.parse(raw) ?? {}) as Partial<NotificationCenterPreference>
    return typeof lastSeenAt === 'string' && parseTimestamp(lastSeenAt) !== null
      ? { found: true, lastSeenAt }
      : { found: false }
  } catch {
    return { found: false }
  }
}

// NB-FR-007: Read the stored last-seen time. "Not found" (first time) is told apart from a
// failure, which rethrows so callers keep their last known value.
const readStoredLastSeen = async (userId: string): Promise<StoredLastSeen> => {
  try {
    const response = await preferenceStore.getUserPreference(
      userId,
      UserPreferences.NotificationCenter,
    )
    return parseStoredValue((response as { value?: string } | null)?.value)
  } catch (err) {
    if (isNotFound(err)) return { found: false }
    throw err
  }
}

// NB-FR-007: Saves for one user run one at a time in this tab, so two read-then-save steps (bar,
// page, starting point) never interleave and overwrite a later time with an earlier one. Each
// entry is the tail of the queue; it never rejects, so the next save always runs.
const userQueues = new Map<string, Promise<unknown>>()
// Counts finished saves, so a read that started before one finished can tell its "not stored" is
// out of date
let savesFinished = 0

const enqueue = <T>(userId: string, task: () => Promise<T>): Promise<T> => {
  const run = (userQueues.get(userId) ?? Promise.resolve()).then(task)
  const settled = run.catch(() => undefined)
  userQueues.set(userId, settled)
  settled.then(() => {
    if (userQueues.get(userId) === settled) userQueues.delete(userId)
  })
  return run
}

// NB-FR-007: Save `seenAt`, never earlier than the stored value (read first, keep the later).
// Returns the last-seen time now stored.
const saveLastSeenAt = (userId: string, seenAt: string | null): Promise<string | null> =>
  enqueue(userId, async () => {
    const stored = await readStoredLastSeen(userId)
    const storedValue = stored.found ? stored.lastSeenAt : null
    const target = laterOf(storedValue, seenAt)
    if (target && target !== storedValue) {
      await preferenceStore.setUserPreference(userId, UserPreferences.NotificationCenter, {
        lastSeenAt: target,
      } satisfies NotificationCenterPreference)
      savesFinished += 1
    }
    return target
  })

// NB-FR-007: Save the newest seen event's timestamp. Used by the bar and the page.
export const markEventsRead = (userId: string, seenEvents: AuditEvent[]): Promise<string | null> =>
  saveLastSeenAt(userId, newestTimestamp(seenEvents))

interface Options {
  // The first successful event load sets the starting point for a first-time user
  eventsLoaded: boolean
}

export default function useLastSeenEvents(events: AuditEvent[], { eventsLoaded }: Options) {
  const { userId } = useEventSessionScope()
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null)
  const [storedMissing, setStoredMissing] = useState(false)
  const isMounted = useIsMounted()
  // NB-FR-007: One starting-point save at a time; a refresh that still finds nothing stored
  // while it is in flight doesn't start another
  const savingBaseline = useRef(false)
  // NB-FR-007: When the read still waiting for an answer started; polls don't stack on it
  const readStartedAt = useRef<number | null>(null)

  const keepLater = useCallback(
    (value: string | null) => {
      if (isMounted()) setLastSeenAt((current) => laterOf(current, value))
    },
    [isMounted],
  )

  // NB-FR-007: Pick up a value written on another machine. Failures keep the last known value.
  const refresh = useCallback(async () => {
    if (!userId) return
    const startedAt = Date.now()
    if (readStartedAt.current !== null && startedAt - readStartedAt.current < STALE_REQUEST_MS) {
      return
    }
    readStartedAt.current = startedAt
    try {
      const savesBefore = savesFinished
      const stored = await readStoredLastSeen(userId)
      if (!isMounted()) return
      // "Not stored" read before a save finished is out of date; the next poll reads again
      if (!stored.found && savesFinished !== savesBefore) return
      setStoredMissing(!stored.found)
      if (stored.found) keepLater(stored.lastSeenAt)
    } catch {
      // Retried on the next poll
    } finally {
      if (readStartedAt.current === startedAt) readStartedAt.current = null
    }
  }, [userId, keepLater, isMounted])

  // NB-FR-007: Marks the events as read and keeps the saved time
  const markAllRead = useCallback(
    async (seenEvents: AuditEvent[]) => {
      if (!userId) return
      try {
        const target = await markEventsRead(userId, seenEvents)
        if (isMounted()) setStoredMissing(false)
        keepLater(target)
      } catch {
        // Retried on the next drawer open or page load
      }
    },
    [userId, keepLater, isMounted],
  )

  useEffect(() => {
    refresh()
  }, [refresh])

  // NB-FR-007: First-time user — start from the newest loaded event (or now), so the bar starts
  // at 0 unread instead of counting every event in the timeframe. Saved like any other time, so
  // a value another machine stored meanwhile is never moved back.
  useEffect(() => {
    if (!userId || !storedMissing || !eventsLoaded || savingBaseline.current) return
    savingBaseline.current = true
    setStoredMissing(false)
    saveLastSeenAt(userId, newestTimestamp(events) ?? new Date().toISOString())
      .then(keepLater)
      .catch(() => {
        // The next refresh finds it still missing and tries again
      })
      .finally(() => {
        savingBaseline.current = false
      })
  }, [userId, storedMissing, eventsLoaded, events, keepLater])

  const unreadCount = useMemo(() => countNewer(events, lastSeenAt), [events, lastSeenAt])

  return { lastSeenAt, unreadCount, markAllRead, refresh }
}
